import { useCallback, useEffect, useState } from 'react';
import { defineMessage } from '@lingui/macro';

import localCache from '../utils/localCache';
import { STORAGE_KEYS } from '../utils/storageKeys';
import authorizeOn from '../utils/resourceAuthorizer';

export const BETA_FEATURES_EVENT = 'if:beta-features';

export const notifyBetaFeaturesChanged = () =>
  window.dispatchEvent(new Event(BETA_FEATURES_EVENT));

export const BETA_FEATURES = [
  {
    id: 'themesManagement',
    name: defineMessage({ message: 'Themes Management' }),
    description: defineMessage({
      message:
        'Night mode toggle in the top bar and a palette picker on your profile.',
    }),
  },
  {
    id: 'homeLayouts',
    name: defineMessage({ message: 'Home layouts' }),
    description: defineMessage({
      message:
        'Choose how the Integrations home lists integrations. A layout picker appears on your profile.',
    }),
  },
];

const readFlags = () => {
  const stored = localCache.get(STORAGE_KEYS.BETA_FEATURES, {});
  return stored && typeof stored === 'object' ? stored : {};
};

export const isBetaFeatureEnabled = (id) =>
  Boolean(authorizeOn() && readFlags()[id] === true);

const readState = () => ({
  flags: readFlags(),
  isAdmin: Boolean(authorizeOn()),
});

export default function useBetaFeatures() {
  const [state, setState] = useState(readState);

  useEffect(() => {
    const sync = () => setState(readState());
    window.addEventListener(BETA_FEATURES_EVENT, sync);
    return () => window.removeEventListener(BETA_FEATURES_EVENT, sync);
  }, []);

  const setEnabled = useCallback((id, enabled) => {
    localCache.set(STORAGE_KEYS.BETA_FEATURES, {
      ...readFlags(),
      [id]: enabled === true,
    });
    notifyBetaFeaturesChanged();
  }, []);

  const isEnabled = useCallback(
    (id) => state.isAdmin && state.flags[id] === true,
    [state],
  );

  return { features: BETA_FEATURES, isEnabled, setEnabled };
}
